import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus, Search, Filter, TrendingUp, Play, Pause,
  CheckCircle2, XCircle, Loader2, RefreshCw, AlertCircle,
  Megaphone,
} from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

// ─── Status Badge ─────────────────────────────────────────────────────────────

function CampaignStatusBadge({ status }) {
  if (status === 'ENABLED') return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      <CheckCircle2 className="w-3.5 h-3.5" /> Ativa
    </span>
  );
  if (status === 'REMOVED') return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
      <XCircle className="w-3.5 h-3.5" /> Removida
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      <Pause className="w-3.5 h-3.5" /> Pausada
    </span>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }) {
  const isLong = String(value).length > 10;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 min-w-[120px]">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{label}</p>
      <p className={`font-bold text-slate-900 dark:text-slate-100 leading-tight ${isLong ? 'text-lg' : 'text-2xl'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const Campaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [savedAccounts, setSavedAccounts] = useState([]);
  const [selectedSavedAccount, setSelectedSavedAccount] = useState(null);
  const [availableStructure, setAvailableStructure] = useState(null);
  const [selectedMcc, setSelectedMcc] = useState(null);
  const [selectedAdsAccount, setSelectedAdsAccount] = useState(null);
  const [loginCustomerId, setLoginCustomerId] = useState(null);

  const [campaigns, setCampaigns] = useState([]);
  const [accountNames, setAccountNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAccount, setFilterAccount] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // ConfirmDialogs
  const [confirmPause, setConfirmPause] = useState(null);
  const [confirmEnable, setConfirmEnable] = useState(null);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => { fetchSavedAccounts(); }, []);

  // ── Fetch functions ────────────────────────────────────────────────────────

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sobevenda_token')}` });

  const fetchSavedAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await fetch('https://api.sobevenda.com.br/api-proxy/affiliate/google-ads-accounts', { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSavedAccounts(data.accounts || []);
        if (data.accounts?.length === 1) handleSelectSavedAccount(data.accounts[0]);
      }
    } catch { setError('Erro ao carregar contas Google Ads'); }
    finally { setLoadingAccounts(false); }
  };

  const handleSelectSavedAccount = async (account) => {
    setSelectedSavedAccount(account);
    setAvailableStructure(null);
    setSelectedMcc(null);
    setSelectedAdsAccount(null);
    setCampaigns([]);
    setAccountNames({});
    await fetchAvailableStructure(account.id);
  };

  const fetchAvailableStructure = async (googleAdsAccountId) => {
    setLoadingStructure(true);
    setError('');
    try {
      const res = await fetch(`https://api.sobevenda.com.br/api-proxy/google-ads/accounts-structure?google_ads_accounts_id=${googleAdsAccountId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.mccs?.length > 0) {
          setAvailableStructure({ mccs: data.mccs.map(mcc => ({ customer_id: mcc.customer_id, account_name: mcc.account_name, accounts: data.accounts })) });
        } else if (data.accounts?.length > 0) {
          const first = data.accounts[0];
          const direct = { customer_id: first.customer_id, name: first.descriptiveName };
          setSelectedAdsAccount(direct);
          fetchCampaigns(direct.customer_id, null);
        }
      }
    } catch { setError('Erro ao buscar estrutura da conta'); }
    finally { setLoadingStructure(false); }
  };

  const handleSelectMcc = (mcc) => {
    setSelectedMcc(mcc);
    setSelectedAdsAccount(null);
    setLoginCustomerId(mcc.customer_id);
    fetchAllMccCampaigns(mcc.customer_id);
  };

  const handleSelectAdsAccount = (account) => {
    setSelectedAdsAccount(account);
    const lcid = selectedMcc?.customer_id || null;
    fetchCampaigns(account.customer_id, lcid);
  };

  const fetchCampaigns = async (customerId, lcid = null, showRefresh = false) => {
    if (!customerId) return;
    showRefresh ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      let url = `https://api.sobevenda.com.br/api-proxy/google-ads/campaigns?customer_id=${customerId}`;
      if (lcid) url += `&login_customer_id=${lcid}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Erro ao buscar campanhas');
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (err) { setError(err.message || 'Erro ao carregar campanhas'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const fetchAllMccCampaigns = async (mccCustomerId, showRefresh = false) => {
    if (!mccCustomerId) return;
    showRefresh ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const res = await fetch(`https://api.sobevenda.com.br/api-proxy/google-ads/campaigns-all-mcc?mcc_customer_id=${mccCustomerId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Erro ao buscar campanhas da MCC');
      const data = await res.json();
      setCampaigns(data.campaigns || []);
      setAccountNames(data.account_names || {});
    } catch (err) { setError(err.message || 'Erro ao carregar campanhas da MCC'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  const handlePauseCampaign = async () => {
    const campaign = confirmPause;
    setConfirmPause(null);
    setActionLoading(campaign.id);
    try {
      const customerId = campaign.account_id || selectedAdsAccount?.customer_id || selectedMcc?.customer_id;
      const lcid = selectedMcc?.customer_id || null;
      if (!customerId) throw new Error('Conta não selecionada');

      const res = await fetch('https://api.sobevenda.com.br/api-proxy/google-ads/campaign-disable', {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ google_ads_accounts_id: selectedSavedAccount.id, campaign_id: campaign.id, customer_id: customerId, login_customer_id: lcid }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message || 'Erro ao pausar campanha'); }

      setSuccessMessage('Campanha pausada com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (selectedAdsAccount) fetchCampaigns(selectedAdsAccount.customer_id, lcid, true);
      else if (selectedMcc) fetchAllMccCampaigns(selectedMcc.customer_id, true);
    } catch (err) { setError(err.message || 'Erro ao pausar campanha'); setTimeout(() => setError(''), 5000); }
    finally { setActionLoading(null); }
  };

  const handleEnableCampaign = async () => {
    const campaign = confirmEnable;
    setConfirmEnable(null);
    setActionLoading(campaign.id);
    try {
      const customerId = campaign.account_id || selectedAdsAccount?.customer_id || selectedMcc?.customer_id;
      const lcid = selectedMcc?.customer_id || null;
      if (!customerId) throw new Error('Conta não selecionada');

      const res = await fetch('https://api.sobevenda.com.br/api-proxy/google-ads/campaign-enable', {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ google_ads_accounts_id: selectedSavedAccount.id, campaign_id: campaign.id, customer_id: customerId, login_customer_id: lcid }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message || 'Erro ao ativar campanha'); }

      setSuccessMessage('Campanha ativada com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (selectedAdsAccount) fetchCampaigns(selectedAdsAccount.customer_id, lcid, true);
      else if (selectedMcc) fetchAllMccCampaigns(selectedMcc.customer_id, true);
    } catch (err) { setError(err.message || 'Erro ao ativar campanha'); setTimeout(() => setError(''), 5000); }
    finally { setActionLoading(null); }
  };

  // ── Computed ───────────────────────────────────────────────────────────────

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? true;
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesAccount = filterAccount === 'all' || c.account_id === filterAccount;
    return matchesSearch && matchesStatus && matchesAccount;
  });

  const stats = {
    total: campaigns.length,
    enabled: campaigns.filter(c => c.status === 'ENABLED').length,
    paused: campaigns.filter(c => c.status === 'PAUSED').length,
  };

  const metrics = filteredCampaigns.reduce((acc, c) => ({
    totalConversions: acc.totalConversions + (c.conversions || 0),
    totalCost: acc.totalCost + (c.cost || 0),
    totalRevenue: acc.totalRevenue + (c.revenue || 0),
  }), { totalConversions: 0, totalCost: 0, totalRevenue: 0 });

  const hasAccount = selectedAdsAccount || selectedMcc;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Megaphone className="w-6 h-6" />
              Campanhas Google Ads
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie suas campanhas de anúncios.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (selectedAdsAccount) fetchCampaigns(selectedAdsAccount.customer_id, selectedMcc?.customer_id || null, true);
                else if (selectedMcc) fetchAllMccCampaigns(selectedMcc.customer_id, true);
              }}
              disabled={!hasAccount || refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </button>

            <button
              onClick={() => navigate('/campanhas/criar')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <Plus className="w-4 h-4" /> Nova Campanha
            </button>
          </div>
        </div>

        {/* Mensagens */}
        {successMessage && (
          <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-900/20 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> {successMessage}
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* SELEÇÃO DE CONTA */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4">Selecionar conta</h2>

          {loadingAccounts ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando contas...
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className={labelCls}>Conta Google Ads</label>
                <select className={inputCls}
                  value={selectedSavedAccount?.id || ''}
                  onChange={(e) => {
                    const acc = savedAccounts.find(a => a.id === parseInt(e.target.value));
                    if (acc) handleSelectSavedAccount(acc);
                  }}>
                  <option value="">Selecione uma conta</option>
                  {savedAccounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.email || a.account_name || `Conta ${a.id}`}{a.google_id ? ` · ${a.google_id}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {loadingStructure && (
                <div className="flex items-center gap-2 text-sm text-slate-500 self-end pb-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando estrutura...
                </div>
              )}

              {availableStructure?.mccs?.length > 0 && (
                <div>
                  <label className={labelCls}>MCC (Manager Account)</label>
                  <select className={inputCls}
                    value={selectedMcc?.customer_id || ''}
                    onChange={(e) => {
                      const mcc = availableStructure.mccs.find(m => m.customer_id === e.target.value);
                      if (mcc) handleSelectMcc(mcc);
                    }}>
                    <option value="">Selecione uma MCC</option>
                    {availableStructure.mccs.map(mcc => (
                      <option key={mcc.customer_id} value={mcc.customer_id}>
                        {mcc.account_name} · {mcc.customer_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedMcc?.accounts?.length > 1 && (
                <div>
                  <label className={labelCls}>Conta de Anúncios</label>
                  <select className={inputCls}
                    value={selectedAdsAccount?.customer_id || ''}
                    onChange={(e) => {
                      const acc = selectedMcc.accounts.find(a => a.customer_id === e.target.value);
                      if (acc) handleSelectAdsAccount(acc);
                    }}>
                    <option value="">Selecione uma conta</option>
                    {selectedMcc.accounts.map(a => (
                      <option key={a.customer_id} value={a.customer_id}>
                        {a.descriptiveName} · {a.customer_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedMcc && campaigns.length > 0 && (
                <div>
                  <label className={labelCls}>Filtrar por conta</label>
                  <select className={inputCls} value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)}>
                    <option value="all">Todas as contas ({[...new Set(campaigns.map(c => c.account_id))].length})</option>
                    {[...new Set(campaigns.map(c => c.account_id))].sort().map(accountId => {
                      const count = campaigns.filter(c => c.account_id === accountId).length;
                      const name = accountNames[accountId] || `Conta ${accountId}`;
                      return <option key={accountId} value={accountId}>{name} ({count})</option>;
                    })}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        {/* STATS */}
        {hasAccount && campaigns.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label="Total" value={stats.total} sub="campanhas" />
            <StatCard label="Ativas" value={stats.enabled} sub="em execução" />
            <StatCard label="Pausadas" value={stats.paused} sub="temporariamente" />
            <StatCard label="Conversões" value={metrics.totalConversions.toLocaleString('pt-BR')} sub="total de leads" />
            <StatCard label="Custo" value={`R$ ${metrics.totalCost.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="investimento" />
            <StatCard label="Faturamento" value={`R$ ${metrics.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="receita gerada" />
          </div>
        )}

        {/* FILTROS */}
        {hasAccount && (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar campanhas..."
                className={`${inputCls} pl-9`}
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={`${inputCls} pl-9 min-w-[180px]`}
              >
                <option value="all">Todos os status</option>
                <option value="ENABLED">Ativas</option>
                <option value="PAUSED">Pausadas</option>
                <option value="REMOVED">Removidas</option>
              </select>
            </div>
          </div>
        )}

        {/* LISTA DE CAMPANHAS */}
        {!hasAccount ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Selecione uma conta</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Escolha uma conta Google Ads acima para visualizar as campanhas.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-slate-400 animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-500">Carregando campanhas...</p>
            </div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center">
            <TrendingUp className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {searchTerm || filterStatus !== 'all' ? 'Nenhuma campanha encontrada' : 'Nenhuma campanha criada'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              {searchTerm || filterStatus !== 'all'
                ? 'Tente ajustar os filtros ou buscar por outro termo.'
                : 'Comece criando sua primeira campanha no Google Ads.'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button onClick={() => navigate('/campanhas/criar')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                <Plus className="w-4 h-4" /> Criar Primeira Campanha
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCampaigns.map((campaign) => (
              <div key={campaign.id}
                className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">

                {/* Header da campanha */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 break-words">{campaign.name}</h3>
                      <CampaignStatusBadge status={campaign.status} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-1">
                      <span>Orçamento: <span className="font-medium text-slate-700 dark:text-slate-300">{campaign.currency}{campaign.budget || 0}/dia</span></span>
                      <span>Estratégia: <span className="font-medium text-slate-700 dark:text-slate-300">{campaign.bidding_strategy || 'Manual'}</span></span>
                      {campaign.target_cpa && <span>CPA Alvo: <span className="font-medium text-slate-700 dark:text-slate-300">{campaign.currency} {campaign.target_cpa}</span></span>}
                      {campaign.target_roas && <span>ROAS Alvo: <span className="font-medium text-slate-700 dark:text-slate-300">{campaign.target_roas}</span></span>}
                    </div>
                  </div>

                  {/* Botão de ação */}
                  <div className="shrink-0">
                    {campaign.status === 'ENABLED' ? (
                      <button
                        onClick={() => setConfirmPause(campaign)}
                        disabled={actionLoading === campaign.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm disabled:opacity-50"
                      >
                        {actionLoading === campaign.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
                        Pausar
                      </button>
                    ) : campaign.status === 'PAUSED' ? (
                      <button
                        onClick={() => setConfirmEnable(campaign)}
                        disabled={actionLoading === campaign.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm disabled:opacity-50"
                      >
                        {actionLoading === campaign.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        Ativar
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Métricas */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                  {[
                    { label: 'Impressões', value: parseInt(campaign.impressions || 0).toLocaleString('pt-BR') },
                    { label: 'Cliques', value: parseInt(campaign.clicks || 0).toLocaleString('pt-BR') },
                    { label: 'Conversões', value: (campaign.conversions || 0).toLocaleString('pt-BR') },
                    { label: 'Custo', value: `${campaign.currency} ${(campaign.cost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                    { label: 'Faturamento', value: `${campaign.currency} ${(campaign.revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, wide: true },
                  ].map(({ label, value, wide }) => (
                    <div key={label} className={`rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-3 ${wide ? 'col-span-2 sm:col-span-1' : ''}`}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</p>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{value}</p>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-slate-400">
                    ID: <span className="font-mono text-slate-500 dark:text-slate-300">{campaign.id}</span>
                    {campaign.account_id && accountNames[campaign.account_id] && (
                      <span className="ml-3">Conta: <span className="text-slate-500 dark:text-slate-300">{accountNames[campaign.account_id]}</span></span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={confirmPause !== null}
        title="Pausar campanha"
        message={`Deseja pausar a campanha "${confirmPause?.name}"?`}
        confirmLabel="Pausar"
        destructive={false}
        onConfirm={handlePauseCampaign}
        onCancel={() => setConfirmPause(null)}
      />
      <ConfirmDialog
        open={confirmEnable !== null}
        title="Ativar campanha"
        message={`Deseja ativar a campanha "${confirmEnable?.name}"?`}
        confirmLabel="Ativar"
        destructive={false}
        onConfirm={handleEnableCampaign}
        onCancel={() => setConfirmEnable(null)}
      />
    </div>
  );
};

export default Campaigns;